import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion"; // For animations
import empty_box from "../../images/empty_box.png";
import Client from "../../utils/client";
import Skelton from "../../icons/skelton"; // Assuming this is your Skeleton loader
import { useTranslation } from "react-i18next";
// import { t } from "i18next";

const Notifications = () => {
    const { t } = useTranslation();
    const [selectedNotification, setSelectedNotification] = useState(null);
    const queryClient = useQueryClient();

    // Fetch notifications
    const { data: notifications, isFetching } = useQuery({
        queryKey: ["notifications"],
        queryFn: async () => {
            const response = await Client.get("/notifications").then(
                (res) => res.data
            );
            return response;
        },
        initialData: [],
    });

    // Mutation to mark a notification as read
    const markAsReadMutation = useMutation({
        mutationFn: (notificationId) =>
            Client.patch(`/notifications/${notificationId}/mark-as-read`),
        onSuccess: (_data, notificationId) => {
            queryClient.setQueryData(["notifications"], (old) =>
                old?.map((n) =>
                    n.id === notificationId ? { ...n, seen: true } : n
                )
            );
            queryClient.invalidateQueries({ queryKey: ["unreadNotifications"] });
        },
        onError: (error) => {
            console.error("Error marking notification as read:", error.response?.data || error.message);
        },
    });

    // Handle notification click
    const handleNotificationClick = (notificationId) => {
        setSelectedNotification(notificationId);
        markAsReadMutation.mutate(notificationId);
    };

    return (
        <div className="w-full px-4 lg:px-20 relative">
            <h1 className="text-end font-bold text-2xl lg:text-3xl my-10 text-primary">
                {/* الإشعارات */}
                {t("sidebar.notifications")}
            </h1>
            {isFetching ? (
                <div dir="rtl" className="my-16 grid grid-cols-1 gap-4 px-0 lg:grid-cols-1 lg:mr-8">
                    {[...Array(5).keys()].map((_, i) => (
                        <div key={i} className="flex flex-row-reverse items-center justify-between gap-3 p-4 bg-gray-100 border rounded-xl shadow-sm animate-pulse">
                            <div className="flex flex-col text-right w-full">
                                <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div> {/* Title */}
                                <div className="h-3 bg-gray-300 rounded w-1/2"></div> {/* Notification message */}
                            </div>
                            <div className="h-3 bg-gray-300 rounded w-16"></div> {/* Date */}
                        </div>
                    ))}
                </div>
            ) : notifications?.length === 0 ? (
                <div className="flex flex-col col-span-2 mt-10 justify-center items-center text-center lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <img
                            src={empty_box}
                            alt="No Notifications"
                            className="w-40 lg:w-48"
                        />
                    </motion.div>
                    <div className="font-bold mt-3 text-lg text-gray-700">
                        {/* لا توجد إشعارات */}
                        {t("sidebar.no_notifications")}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {notifications?.map((notification) => (
                        <motion.div
                            key={notification.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.1 * notifications.indexOf(notification) }}
                        >
                            <Link
                                to={
                                    notification.type.toLowerCase() === "order"
                                        ? `/home/profile/order/${notification.referenceId}`
                                        : `/home/${notification.type.toLowerCase()}/${notification.referenceId}`
                                } // Assuming you have a route to view detailed notifications
                                onClick={() => handleNotificationClick(notification.id)}
                                className={`
                                    flex flex-row-reverse items-center justify-between gap-3 p-4
                                    rounded-xl shadow-sm transition-all hover:shadow-md hover:scale-[1.02]
                                    ${notification.seen ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-400'}
                                    ${selectedNotification === notification.id ? 'border-primary' : ''}
                                `}
                            >
                                {/* Square Image */}
                                <div className="flex-shrink-0 relative">
                                    <img
                                        src={notification.image_url || empty_box} // Assuming notification might have image_url
                                        alt="Notification"
                                        className="w-14 h-14 rounded-xl object-cover border border-gray-200"
                                    />
                                    {!notification.seen && (
                                        <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                            ! {/* New notification marker */}
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col text-right w-full">
                                    <div className="font-bold text-lg text-gray-800">
                                        {notification.message}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {new Date(notification.createdAt).toLocaleDateString("ar-EG", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                        })}
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Notifications;